/**
 * useDaily — wraps /daily/today + the /start nonce ceremony + /submit.
 *
 * The /start call burns one stamina point and returns a server-signed nonce
 * that must be echoed in /submit. The 409 path (NO_STAMINA, GAME_IN_PROGRESS)
 * is surfaced as a typed `{ ok: false, reason }` result so the caller can
 * show a toast without crashing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  DailySubmitError,
  DailySubmitRequest,
  DailySubmitResponse,
  DailyTodayResponse,
  Move,
} from '@sagwa/types'
import { apiClient, type DailyStartResult } from '../api/client'

export type DailyStartContext = {
  startedAtMs: number
  nonce: string
  seed: number
  date: string
}

export type UseDailyResult = {
  today: DailyTodayResponse | null
  start: () => Promise<DailyStartResult>
  submit: (
    clientFinalScore: number,
    moves: Move[],
    context: { startedAtMs: number; nonce: string; date: string },
  ) => Promise<DailySubmitResponse | DailySubmitError>
  refetchToday: () => Promise<void>
  loading: boolean
  error: Error | null
}

export function useDaily(): UseDailyResult {
  const [today, setToday] = useState<DailyTodayResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(true)

  const refetchToday = useCallback(async (): Promise<void> => {
    try {
      const resp = await apiClient.getDailyToday()
      if (!mountedRef.current) return
      setToday(resp)
      setError(null)
    } catch (err) {
      if (!mountedRef.current) return
      setError(err instanceof Error ? err : new Error(String(err)))
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    setLoading(true)
    void refetchToday().finally(() => {
      if (mountedRef.current) setLoading(false)
    })
    return () => {
      mountedRef.current = false
    }
  }, [refetchToday])

  const start = useCallback(async (): Promise<DailyStartResult> => {
    return apiClient.dailyStart()
  }, [])

  const submit = useCallback(
    async (
      clientFinalScore: number,
      moves: Move[],
      context: { startedAtMs: number; nonce: string; date: string },
    ): Promise<DailySubmitResponse | DailySubmitError> => {
      const payload: DailySubmitRequest = {
        date: context.date,
        startedAtMs: context.startedAtMs,
        nonce: context.nonce,
        clientFinalScore,
        moves,
      }
      return apiClient.dailySubmit(payload)
    },
    [],
  )

  // IMPORTANT: stable identity. DailyChallenge.tsx uses this object in a
  // useEffect dependency array; if we returned a fresh object literal each
  // render the effect would re-run on every game:tick (every ~100 ms via
  // setRemainingMs), reset movesRef back to [], and the daily/submit body
  // would land with `moves: []` — server replay then rejects with
  // SCORE_MISMATCH because replay score (0) doesn't match clientFinalScore.
  return useMemo(
    () => ({ today, start, submit, refetchToday, loading, error }),
    [today, start, submit, refetchToday, loading, error],
  )
}
